import { QTableProps } from 'quasar';
import { FetchListFilterOptions, FetchListOptions } from '~/api/_types';
import { NotificationInfo } from '~/api/notifications/_types';
import { NotificationActionType } from '~/enums/_types';
import { t } from '~/plugins/i18n';
import notificationManagerInstance from '~/plugins/notificationManager';
import { NotificationActionInfo } from '~/plugins/notificationManager/_types';
import { fetchNotifications } from '~/services/notifications';
import { useNotificationsStore } from '~/stores/notifications';

const useNotifications = (
    initialPagination?: QTableProps['pagination'] | null,
    implicitFilter?: FetchListFilterOptions[] | null
) => {
    const notificationsStore = useNotificationsStore();
    const { status, needNotificationsRefresh } = storeToRefs(notificationsStore);
    const hasUnreadNotifications = computed(() => !!status.value?.Unread);

    const { data, pagination, userFilter, isLoading, request } = useApiCollectionDataObject(
        async (opt?: FetchListOptions) => {
            return await fetchNotifications(opt);
        },
        initialPagination,
        implicitFilter
    );

    watch(needNotificationsRefresh, async (newValue) => {
        if (newValue) {
            await request();
            notificationsStore.setNotificationsRefresh(false);
        }
    });

    const getActions = (info: NotificationInfo): NotificationActionInfo[] => {
        return notificationManagerInstance.getActions(info.Type);
    };

    const getNotificationTitle = (info: NotificationInfo): string => {
        switch (info.Type) {
            case NotificationActionType.OrderConfirmed:
            case NotificationActionType.OrderInvoice:
            case NotificationActionType.OrderReady:
                if (
                    info.AdditionalInfo != null &&
                    typeof info.AdditionalInfo === 'object' &&
                    'DocumentNumber' in info.AdditionalInfo
                ) {
                    return t(`notifications.actions.${info.Type}.title`, {
                        id: info.AdditionalInfo.DocumentNumber,
                    });
                }
                return '';
            case NotificationActionType.ServiceCallCompleted:
            case NotificationActionType.ServiceCallEvidenced:
                if (
                    info.AdditionalInfo != null &&
                    typeof info.AdditionalInfo === 'object' &&
                    'Id' in info.AdditionalInfo
                ) {
                    return t(`notifications.actions.${info.Type}.title`, { id: info.AdditionalInfo.Id });
                }
                return '';
        }
    };

    const getNotificationBody = (info: NotificationInfo): string => {
        switch (info.Type) {
            case NotificationActionType.OrderConfirmed:
            case NotificationActionType.OrderInvoice:
            case NotificationActionType.OrderReady:
            case NotificationActionType.ServiceCallCompleted:
            case NotificationActionType.ServiceCallEvidenced:
                return '';
        }
    };

    return {
        status,
        hasUnreadNotifications,
        data,
        pagination,
        userFilter,
        isLoading,
        request,
        getActions,
        getNotificationTitle,
        getNotificationBody,
    };
};

export default useNotifications;
